/**
 * Leads (13 Oct 2026): the wire shapes and the small readings every screen of
 * the owner's lead management shares. Kept in no book — every call pins the
 * session's own (`book: null`), like tasks.
 */
import { useQuery } from '@tanstack/react-query';
import {
  LEAD_HEADING_SUGGESTIONS,
  LEAD_OPEN_STATUSES,
  callsLeads,
  managesLeads,
  type LeadCallResultKey,
  type LeadSplitPerson,
  type LeadStatusKey,
  type LeadTaker,
  type LeadTransfer,
  type LeadTransferOutcome,
} from '@bright/shared';

import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { displayName } from '@/lib/format';
import { OUTCOME_TONE } from '../enquiries/enquiries-api';
import { agoLabel } from '../enquiries/enquiry-desk-api';

export { agoLabel };

export const NO_BOOK = { book: null } as const;

export interface LeadRow {
  id: string;
  name: string | null;
  phone: string;
  details: Array<{ label: string; value: string }>;
  /** Its sheet, and the heading the sheet is filed under (2 Nov 2026). */
  list: { id: string; name: string; heading: string | null };
  status: LeadStatusKey;
  owner: { id: string; name: string } | null;
  mine: boolean;
  takenAt: string | null;
  nextCallAt: string | null;
  lastCallAt: string | null;
  calls: number;
  lastResult: LeadCallResultKey | null;
  lastNote: string | null;
  closedAt: string | null;
  createdAt: string;
  /** Handed over for conversion (19 Oct 2026). */
  transfer: LeadTransfer | null;
}

export interface LeadDetail extends LeadRow {
  columns: string[];
  history: Array<{
    id: string;
    kind: 'TAKEN' | 'CALL' | 'MOVED' | 'RETURNED' | 'TRANSFERRED';
    at: string;
    by: string | null;
    result: LeadCallResultKey | null;
    note: string | null;
    nextCallAt: string | null;
    to: string | null;
  }>;
}

export type CallerTab = 'new' | 'today' | 'mine' | 'transferred' | 'done';
export type ManagerTab = 'pool' | 'working' | 'interested' | 'transferred' | 'converted' | 'closed' | 'all';
export type LeadTab = CallerTab | ManagerTab;

export interface LeadPage {
  data: LeadRow[];
  /** `searching`: a search is on, and the rows come from every tab the viewer has, not only `tab`. */
  meta: { tab: LeadTab; searching?: boolean; page: number; pageSize: number; total: number; totalPages: number; tabs: Partial<Record<LeadTab, number>> };
}

export interface LeadSheetPlan {
  fileName: string;
  sheetName: string;
  headers: string[];
  headed: boolean;
  nameColumn: number | null;
  phoneColumn: number | null;
  detailColumns: number[];
  counts: { rows: number; ready: number; invalid: number; repeatedInSheet: number; alreadyLeads: number };
  sample: Array<{ row: number; name: string | null; phone: string | null; details: string[]; skip: 'invalid' | 'repeated' | 'already' | null }>;
  invalidRows: number[];
}

export interface LeadListRow {
  id: string;
  name: string;
  heading: string | null;
  fileName: string | null;
  uploadedBy: string | null;
  createdAt: string;
  withdrawnAt: string | null;
  columns: string[];
  rows: number;
  added: number;
  duplicates: number;
  invalid: number;
  pool: number;
  working: number;
  interested: number;
  converted: number;
  notInterested: number;
  wrongNumber: number;
  transferred: number;
  wonAfterTransfer: number;
  called: number;
}

export interface LeadTeam {
  people: Array<{
    id: string;
    name: string;
    active: boolean;
    open: number;
    due: number;
    interested: number;
    converted: number;
    notInterested: number;
    wrongNumber: number;
    transferred: number;
    wonAfterTransfer: number;
    taken: number;
    callsToday: number;
    callsThisMonth: number;
    conversion: number | null;
  }>;
  /** The takers' side of the hand-overs. */
  takers: Array<{ id: string; name: string; handed: number; open: number; won: number; lost: number; due: number; untouched: number }>;
  totals: { pool: number; interested: number; converted: number; callsToday: number; transferred: number; wonAfterTransfer: number };
}

/** One person's part of an even split, as the server dealt it. */
export interface LeadSplitShare {
  id: string;
  name: string;
  count: number;
}

/** A sheet just brought in — with who got how many when it was split. */
export type LeadListAdded = LeadListRow & { split: LeadSplitShare[] | null };

/** The telecalling team, each with the open leads they hold — who a sheet is split between. */
export function useLeadCallers(enabled = true) {
  return useQuery({
    queryKey: leadKeys.callers,
    queryFn: () => api.get<{ data: LeadSplitPerson[] }>('/leads/callers', undefined, NO_BOOK),
    enabled,
    staleTime: 60_000,
  });
}

/** The headings sheets are filed under, newest first. */
export function useLeadHeadings(enabled = true) {
  return useQuery({
    queryKey: leadKeys.headings,
    queryFn: () => api.get<{ data: Array<{ heading: string; sheets: number }> }>('/leads/headings', undefined, NO_BOOK),
    enabled,
    staleTime: 60_000,
  });
}

/** The headings to offer on an upload: those in use, then the starting ones not yet used — one of each, whatever its letters. */
export function headingChoices(used: readonly string[]): string[] {
  const out: string[] = [];
  for (const h of [...used, ...LEAD_HEADING_SUGGESTIONS]) {
    if (!out.some((o) => o.toLowerCase() === h.toLowerCase())) out.push(h);
  }
  return out;
}

/** "Neelam 34 · Sonali 33" — who got how many. */
export const sharesLine = (shares: ReadonlyArray<{ name: string; count: number }>) => shares.map((p) => `${p.name} ${p.count.toLocaleString('en-IN')}`).join(' · ');

/** Who a lead can be handed to. */
export function useLeadTakers(enabled = true) {
  return useQuery({
    queryKey: leadKeys.takers,
    queryFn: () => api.get<{ data: LeadTaker[] }>('/leads/takers', undefined, NO_BOOK),
    enabled,
    staleTime: 60_000,
  });
}

export const leadKeys = {
  all: ['leads'] as const,
  list: (filters: Record<string, string | number | undefined>) => ['leads', 'list', filters] as const,
  detail: (id: string) => ['leads', 'detail', id] as const,
  team: ['leads', 'team'] as const,
  lists: ['leads', 'lists'] as const,
  callers: ['leads', 'callers'] as const,
  headings: ['leads', 'headings'] as const,
  takers: ['leads', 'takers'] as const,
  summary: ['leads', 'summary'] as const,
};

/** The owner (uploads, sees all, moves), the telecalling team (calls), or neither. */
export function useLeadRole(): 'manager' | 'caller' | null {
  const { user } = useAuth();
  if (!user) return null;
  if (managesLeads(user)) return 'manager';
  return callsLeads(user.role.key) ? 'caller' : null;
}

/** The reminder's figures, refreshed while the app is open. */
export function useLeadSummary() {
  const role = useLeadRole();
  return useQuery({
    queryKey: leadKeys.summary,
    queryFn: () => api.get<{ role: 'manager' | 'caller' | null; pool: number; due: number; callsToday: number }>('/leads/summary', undefined, NO_BOOK),
    enabled: role !== null,
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
  });
}

/** Today in India, "YYYY-MM-DD", and a day so many days on. */
export const istDayKey = (offsetDays = 0) => new Date(Date.now() + 330 * 60_000 + offsetDays * 86_400_000).toISOString().slice(0, 10);
export const istKeyOf = (iso: string) => new Date(new Date(iso).getTime() + 330 * 60_000).toISOString().slice(0, 10);
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const dayLabel = (key: string) => `${Number(key.slice(8, 10))} ${SHORT_MONTHS[Number(key.slice(5, 7)) - 1]}`;

/** What an open lead's next call says: overdue, today, or the day. */
export function nextCallState(row: { status: LeadStatusKey; nextCallAt: string | null }): { text: string; tone: 'signal' | 'warning' | 'neutral' } | null {
  if (!isOpenLead(row.status) || !row.nextCallAt) return null;
  const key = istKeyOf(row.nextCallAt);
  const today = istDayKey();
  if (key < today) return { text: `Overdue ${Math.round((Date.parse(today) - Date.parse(key)) / 86_400_000)} d`, tone: 'signal' };
  if (key === today) return { text: 'Call today', tone: 'warning' };
  return { text: `Call ${dayLabel(key)}`, tone: 'neutral' };
}

/** "tel:" and WhatsApp links for a ten-digit number. */
export const reachOf = (phone: string) => ({ call: `tel:+91${phone}`, whatsapp: `https://wa.me/91${phone}` });

/** 98390 12345 — the way a number is read out. */
export const spacedPhone = (phone: string) => (phone.length === 10 ? `${phone.slice(0, 5)} ${phone.slice(5)}` : phone);

/** A status never speaks in the brand accent; a transferred lead's colour is its outcome there. */
export const STATUS_TONE: Record<LeadStatusKey, 'neutral' | 'info' | 'warning' | 'success'> = {
  NEW: 'neutral',
  FOLLOW_UP: 'neutral',
  INTERESTED: 'info',
  CONVERTED: 'success',
  NOT_INTERESTED: 'neutral',
  WRONG_NUMBER: 'neutral',
  TRANSFERRED: 'neutral',
};

/** The next-call chip's tone: overdue speaks in signal, as on Enquiries. */
export const NEXT_TONE = { signal: 'signal', warning: 'warning', neutral: 'neutral' } as const;

/** What a transferred lead's outcome badge says it in: the enquiry book's own tones. */
export const TRANSFER_TONE: Record<LeadTransferOutcome, 'info' | 'success' | 'neutral'> = OUTCOME_TONE;

/** Still on somebody's calling list. */
export const isOpenLead = (status: LeadStatusKey) => LEAD_OPEN_STATUSES.includes(status);

/** Won, the one way it is counted everywhere: converted on the call, or converted after the transfer. */
export const wonOf = (x: { converted: number; wonAfterTransfer: number }) => x.converted + x.wonAfterTransfer;

/** A lead's name as a sheet gives it, set for reading — the number when there is none. */
export const leadName = (row: { name: string | null; phone: string }) => displayName(row.name, spacedPhone(row.phone));

/** The column that names the place, as the transfer reads it — the city, or the first detail. */
const PLACE = /city|town|district|village|area|place|location|shahar|jila/i;
export const placeOf = (details: Array<{ label: string; value: string }>) => (details.find((d) => PLACE.test(d.label)) ?? details[0])?.value ?? null;

/** Whole days since a moment. */
export const daysSince = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

/** Days since the taker last wrote, or since it was transferred when they never have. */
export function quietDays(t: LeadTransfer): number {
  return daysSince(t.lastUpdateAt ?? t.at);
}

/** Where a transferred lead is, when, and whether its taker has written on it yet. */
export function transferFacts(t: LeadTransfer): { to: string; when: string; update: string } {
  const to = t.to?.name ?? 'the field';
  return {
    to,
    when: dayLabel(istKeyOf(t.at)),
    update: t.lastUpdateAt ? `${to} updated it ${agoLabel(t.lastUpdateAt)}` : `${to} has not called yet`,
  };
}
